// src/components/ExamFloatingPanel.jsx
// A draggable, resizable, closable panel rendered inside the exam page -
// NOT a browser window.open() popup. This is what real exam CBE software
// actually does for exhibits/requirements/responses (confirmed against a
// screenshot of the real thing): a small floating chrome (title bar, close
// button) layered over the same page via CSS, not a separate OS window.
// Building it this way sidesteps the popup-vs-tab problem entirely, since
// there's no browser popup involved to misbehave.

import React, { useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';

const MIN_WIDTH = 320;
const MIN_HEIGHT = 220;

// Where a viewport-tracking panel sits: a fixed inset clear of the exam
// page's header (~64px) and footer (~80px) bars.
const viewportPosition = () => ({ x: 40, y: 76 });
const viewportSize = () => ({
  width: Math.max(MIN_WIDTH, window.innerWidth - 80),
  height: Math.max(MIN_HEIGHT, window.innerHeight - 176)
});

const ExamFloatingPanel = ({ title, onClose, defaultPosition, defaultSize, zIndex, onFocus, children, trackViewport }) => {
  // A viewport-tracking panel starts at its full viewport size straight
  // away. (It used to start at defaultSize and resize in an effect after the
  // first paint, so the spreadsheet/word processor visibly jumped from
  // 900x560 to nearly full-screen as it opened.)
  const [position, setPosition] = useState(() => (trackViewport ? viewportPosition() : defaultPosition));
  const [size, setSize] = useState(() => (trackViewport ? viewportSize() : defaultSize));
  const dragRef = useRef(null); // { startX, startY, startLeft, startTop }
  const resizeRef = useRef(null); // { startX, startY, startWidth, startHeight }
  const manuallyResizedRef = useRef(false); // stops viewport-tracking the moment the student resizes it themselves

  const handleTitleMouseDown = useCallback((e) => {
    onFocus();
    manuallyResizedRef.current = true; // dragging counts as customizing it too - stop auto-repositioning from here on
    dragRef.current = { startX: e.clientX, startY: e.clientY, startLeft: position.x, startTop: position.y };
  }, [position, onFocus]);

  const handleResizeMouseDown = useCallback((e) => {
    e.stopPropagation();
    onFocus();
    manuallyResizedRef.current = true;
    resizeRef.current = { startX: e.clientX, startY: e.clientY, startWidth: size.width, startHeight: size.height };
  }, [size, onFocus]);

  // Panels opted into this (the spreadsheet/word processor "main work area"
  // panels) grow and shrink with the browser window itself, not just their
  // own drag-resize handle - matches "if I make the window bigger I want
  // the spreadsheet to get bigger" rather than requiring a second, separate
  // manual resize of the floating panel on top of resizing the window.
  // Also repositions to a fixed inset from the top-left rather than
  // whatever cascading-offset spot it was originally opened at - that
  // offset is fine for a small utility panel (Calculator, Scratch Pad) but
  // a near-fullscreen panel needs to stay clear of the exam page's own
  // header/footer bars (~64px and ~80px respectively) instead of drifting
  // over them. Stops adjusting entirely - both size and position - the
  // moment the student drags or resizes it themselves, so a deliberate
  // change sticks instead of snapping back on the next window resize.
  React.useEffect(() => {
    if (!trackViewport) return;
    const applyViewportSize = () => {
      if (manuallyResizedRef.current) return;
      setPosition(viewportPosition());
      setSize(viewportSize());
    };
    applyViewportSize();
    window.addEventListener('resize', applyViewportSize);
    return () => window.removeEventListener('resize', applyViewportSize);
  }, [trackViewport]);

  const handleMouseMove = useCallback((e) => {
    if (dragRef.current) {
      const { startX, startY, startLeft, startTop } = dragRef.current;
      setPosition({
        x: Math.max(0, startLeft + (e.clientX - startX)),
        y: Math.max(0, startTop + (e.clientY - startY))
      });
    } else if (resizeRef.current) {
      const { startX, startY, startWidth, startHeight } = resizeRef.current;
      setSize({
        width: Math.max(MIN_WIDTH, startWidth + (e.clientX - startX)),
        height: Math.max(MIN_HEIGHT, startHeight + (e.clientY - startY))
      });
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
    resizeRef.current = null;
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <div
      onMouseDown={onFocus}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex,
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'sans-serif'
      }}
    >
      <div
        onMouseDown={handleTitleMouseDown}
        style={{
          background: '#2c3e50', color: 'white',
          padding: '8px 10px', display: 'flex', alignItems: 'center', gap: '8px',
          cursor: 'move', userSelect: 'none', flexShrink: 0
        }}
      >
        <div style={{ flex: 1, fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </div>
        <button
          type="button"
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          title="Close"
          style={{
            background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '4px',
            width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', cursor: 'pointer', flexShrink: 0
          }}
        >
          <X size={14} />
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {children}
      </div>

      <div
        onMouseDown={handleResizeMouseDown}
        title="Resize"
        style={{
          position: 'absolute', right: 0, bottom: 0, width: '16px', height: '16px',
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 50%, #cbd5e0 50%)'
        }}
      />
    </div>
  );
};

export default ExamFloatingPanel;
